import * as Popover from '@radix-ui/react-popover';
import {
  Calendar, Check, CheckSquare, ChevronDown, ChevronRight, Globe, Maximize2, Search, Trash, X, XCircle,
} from 'react-feather';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TFunction } from 'i18next';
import { startOfMonth } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import difference from 'lodash/difference';
import intersection from 'lodash/intersection';
import kebabCase from 'lodash/kebabCase';
import snakeCase from 'lodash/snakeCase';
import sortBy from 'lodash/sortBy';
import times from 'lodash/times';

import { GetCalendarPageQuery as GetCalendarPage, SportType } from '__generated__/graphql';

import { Container } from '../Common/Layout';
import { EventCard, EventCardPlaceholder } from './EventCard';
import { countryCodes } from '../../common/useCountries';
import { mergeUrlParams, toggleInList } from '../../common/helpers';
import { popularSportTypes } from '../../backend/Events/useGroupedSportTypes';
import { useNavigation } from '../Common/useNavigation';
import GetCalendarPageQuery from './GetCalendarPageQuery';
import SportIcon from '../../common/Common/SportIcon';
import UI from '../../common/UI';
import useDocumentTitle from '../../common/useDocumentTitle';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useQuery from '../../api/useQuery';
import useUrlState from '../../common/useUrlState';

interface CalendarPageProps {
  sportType?: SportType;
}

const CalendarPage = ({ sportType: sportTypeProp }: CalendarPageProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const history = useHistory();
  const device = useMediaDevice();

  const activeSportType = sportTypeProp ? snakeCase(sportTypeProp) : null;
  const activeSportTypeIsValid = Object.values(SportType).includes(activeSportType as any);

  const pageTitleKey = popularSportTypes.includes(activeSportType as SportType) ? activeSportType : 'default';

  useDocumentTitle([t(`website.calendar_titles.${pageTitleKey}`), 'Atleta']);

  useEffect(() => {
    if (activeSportType && !activeSportTypeIsValid) {
      history.replace(r('Calendar', { sportType: null }));
    }
  }, [activeSportType, activeSportTypeIsValid, history, r]);

  const [urlState, setUrlState] = useUrlState({
    parse: (params, { integer, array, string, enumeration }) => ({
      ...params,
      search: string(params.search),
      countries: array(
        params.countries,
        (value) => (typeof value === 'string' && countryCodes.includes(value) ? value : null),
      ),
      sport_types: array(params.sport_types, (value) => enumeration(value, SportType)),
      distances: array(params.distances, (value) => enumeration(value, DistancePreset)),
      min_distance: integer(params.min_distance),
      max_distance: integer(params.max_distance),
      months: array(params.months, string),
      page: integer(params.page),
      sort: enumeration(params.sort, SortOption),
    }),
  });

  const activeSportTypes = useMemo(
    () => (urlState.sport_types.length > 0
      ? urlState.sport_types
      : (activeSportTypeIsValid ? [activeSportType] : [])),
    [urlState, activeSportType, activeSportTypeIsValid],
  );

  const filters = useMemo(() => ({
    sport_types: activeSportTypes,
    countries: urlState.countries,
    min_distance: urlState.min_distance,
    max_distance: urlState.max_distance,
    distances: urlState.distances,
    months: urlState.months,
    search: urlState.search,
    page: urlState.page,
    sort: urlState.sort,
  }), [activeSportTypes, urlState]);

  const perPage = device.isSmall ? 10 : 12;

  const { data, loading } = useQuery(GetCalendarPageQuery, {
    variables: {
      ...filters,
      min_distance: filters.min_distance !== null ? filters.min_distance * 1000 : null,
      max_distance: filters.max_distance !== null ? filters.max_distance * 1000 : null,
      per_page: perPage,
    },
    onError: () => {
      history.replace(r('Calendar', { sportType: null }));
    },
  });

  const handleSportTypesChange = useCallback((value: FiltersValue) => {
    if (value.sport_types.length === 0) {
      history.replace(`${r('Calendar', { sportType: null })}${mergeUrlParams({ sport_types: null, page: null })}`);
      return;
    }

    if (value.sport_types.length === 1) {
      history.replace(`${r('Calendar', { sportType: kebabCase(value.sport_types[0]) })}${mergeUrlParams({ sport_types: null, page: null })}`);
      return;
    }

    history.replace(`${r('Calendar', { sportType: null })}${mergeUrlParams({ sport_types: value.sport_types, page: null })}`);
  }, [history, r]);

  const handleFilterChange = useCallback((value: Partial<typeof urlState>) => {
    setUrlState((urlState) => {
      const newState: typeof urlState = { ...urlState, ...value, page: null };

      if (newState.sport_types.length === 1) {
        newState.sport_types = [];
      }

      return newState;
    }, true);
  }, [setUrlState]);

  const events = data?.publicEvents;
  const sportTypes = data?.sportTypes;
  const countries = data?.eventCountries;

  const sortedCountries = useMemo(
    () => sortBy(countries || [], (country) => t(`common:countries.${country}`)),
    [countries, t],
  );

  return (
    <>
      <Container size="lg" role="main">
        <UI.Div>
          <UI.H1 sc={{ mb: 1 }}>
            {t(`website.calendar_titles.${pageTitleKey}`)}
          </UI.H1>
          <UI.Paragraph sc={{ big: true }} style={{ maxWidth: 800 }}>
            {t(`website.calendar_descriptions.${pageTitleKey}`)}
          </UI.Paragraph>
        </UI.Div>

        <UI.FlexContainer sc={{ wrap: 'wrap', mt: -2 }}>
          <SearchFilters
            value={filters}
            onChange={handleFilterChange}
          />

          <SportTypeFilters
            value={filters}
            onChange={handleSportTypesChange}
            sportTypes={sportTypes}
          />

          <DistanceFilters
            value={filters}
            onChange={handleFilterChange}
          />

          <CountryFilters
            value={filters}
            onChange={handleFilterChange}
            countries={sortedCountries}
          />

          <DateFilters
            value={urlState}
            onChange={handleFilterChange}
          />

          <SortFilters
            value={filters}
            onChange={handleFilterChange}
          />
        </UI.FlexContainer>

        {!data && (
          <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr', '1fr 1fr 1fr', '1fr 1fr 1fr'], loading }}>
            {times(perPage, (index) => (
              <EventCardPlaceholder key={index} />
            ))}
          </UI.GridContainer>
        )}

        {data && (
          <>
            {events.data.length > 0 && (
              <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr', '1fr 1fr 1fr', '1fr 1fr 1fr'], loading }}>
                {events.data.map((event) => (
                  <EventCard event={event} key={event.id} />
                ))}
              </UI.GridContainer>
            )}

            {events.total === 0 && (
              <UI.Paragraph sc={{ mb: 0, big: true }}>
                {t('website.no_events_found')}
              </UI.Paragraph>
            )}

            {events.last_page > 1 && (
              <UI.Pager pageCount={events.last_page} />
            )}
          </>
        )}
      </Container>

      <UI.HR sc={{ my: 0 }} />

      <UI.Div>
        <Container size="lg">
          <UI.Div>
            <UI.H2 sc={{ fontWeight: 700 }}>
              {t('website.quick_links')}
            </UI.H2>
          </UI.Div>

          <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr', '1fr 1fr 1fr'], gutter: [0.5, 1] }}>
            {popularSportTypes.map((sportType) => (
              <UI.NavCard
                to={r('Calendar', { sportType: kebabCase(sportType) })}
                sc={{
                  big: true, outline: 'gray.250', background: sportType === activeSportType ? 'gray.150' : undefined,
                }}
                title={t(`website.calendar_titles.${sportType}`)}
                key={sportType}
              >
                <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between', spaceX: 2 }}>
                  <UI.Icon sc={{ size: 2 }}>
                    <SportIcon type={sportType} />
                  </UI.Icon>
                  <UI.H3>
                    {t(`website.calendar_titles.${sportType}`)}
                  </UI.H3>
                  <UI.Icon style={{ marginLeft: 'auto' }}>
                    <ChevronRight />
                  </UI.Icon>
                </UI.FlexContainer>
              </UI.NavCard>
            ))}
          </UI.GridContainer>
        </Container>
      </UI.Div>
    </>
  );
};

interface SportTypeFiltersProps extends FiltersProps {
  sportTypes?: SportType[];
}

const SportTypeFilters = ({ value: defaultValue, onChange, sportTypes = [] }: SportTypeFiltersProps) => {
  const { t } = useTranslation();
  const device = useMediaDevice();

  const [value, setValue] = useState(defaultValue);
  const [showing, setShowing] = useState(false);

  const initialTab = intersection(
    defaultValue.sport_types,
    difference(sportTypes, popularSportTypes),
  ).length > 0 ? 'all' : 'popular';

  const [tab, setTab] = useState<'popular' | 'all'>(initialTab);

  const toggleSportType = (sportType: SportType) => {
    setValue((value) => ({
      ...value,
      sport_types: toggleInList(value.sport_types, sportType),
    }));
  };

  const isEmpty = defaultValue.sport_types.length === 0;
  const hasChanged = JSON.stringify(defaultValue) !== JSON.stringify(value);

  const reset = () => {
    const emptyValue: FiltersValue = {
      ...defaultValue,
      sport_types: [],
    };

    setValue(emptyValue);

    if (!showing) {
      onChange(emptyValue);
    }
  };

  const confirm = () => {
    onChange({
      ...value,
      sport_types: tab === 'all' ? value.sport_types : intersection(popularSportTypes, value.sport_types),
    });
    setShowing(false);
  };

  const handleOpen = (showing: boolean) => {
    setShowing(showing);
    setValue(defaultValue);

    if (showing) {
      setTab(initialTab);
    }
  };

  return (
    <UI.Pill sc={{ mr: 1.5, mt: 1.5 }}>
      <Popover.Root open={showing} onOpenChange={handleOpen}>
        <Popover.Trigger asChild>
          <UI.Button
            sc={{
              outline: true,
              brand: 'gray.250',
              color: 'gray.800',
              pl: 1.5,
              pr: isEmpty ? 1.5 : 0.5,
              active: !isEmpty || showing,
            }}
          >
            <UI.Icon>
              <CheckSquare />
            </UI.Icon>
            {' '}
            {isEmpty && t('website.sport')}
            <UI.Delimit delimiter=", ">
              {...defaultValue.sport_types.map((sportType) => t(`common:sport_types.${sportType}`)).slice(0, 3) || [t('website.sport')]}
              {defaultValue.sport_types.length > 3 && '...'}
            </UI.Delimit>
            {isEmpty && (
              <UI.Icon sc={{ ml: 1.5 }}>
                <ChevronDown />
              </UI.Icon>
            )}
          </UI.Button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content style={{ zIndex: 1000 }} side="bottom" align="start">
            <UI.Form onSubmit={() => confirm()}>
              <UI.Card
                sc={{
                  borderRadius: 'md', hasPanels: true, active: true, width: '600px', background: 'white',
                }}
                style={{ maxWidth: device.width - 32 }}
              >
                {sportTypes.length > 0 && (
                  <UI.Div sc={{ py: 0, spaceX: 2 }} style={{ margin: '2px 0 -1px 0' }}>
                    <UI.TabButton
                      type="button"
                      onClick={() => setTab('popular')}
                      sc={{ active: tab === 'popular' }}
                    >
                      {t('website.popular')}
                    </UI.TabButton>
                    <UI.TabButton
                      type="button"
                      onClick={() => setTab('all')}
                      sc={{ active: tab === 'all' }}
                    >
                      {t('website.all')}
                    </UI.TabButton>
                  </UI.Div>
                )}
                {tab === 'popular' && (
                  <UI.FadeIn>
                    <UI.FormGrid sc={{ gutter: 0.25, columns: ['1fr 1fr', '1fr 1fr 1fr'] }}>
                      {popularSportTypes.map((sportType) => (
                        <UI.Button
                          onClick={() => toggleSportType(sportType)}
                          sc={{
                            outline: true,
                            block: true,
                            background: value.sport_types.includes(sportType) ? 'gray.150' : 'transparent',
                            fontWeight: 400,
                          }}
                          key={sportType}
                        >
                          {t(`common:sport_types.${sportType}`)}
                        </UI.Button>
                      ))}
                    </UI.FormGrid>
                  </UI.FadeIn>
                )}
                {tab === 'all' && (
                  <UI.FadeIn>
                    <UI.FormGrid sc={{ gutter: 0.25, columns: ['1fr 1fr', '1fr 1fr 1fr'] }}>
                      {sportTypes.map((sportType) => (
                        <UI.Button
                          onClick={() => toggleSportType(sportType)}
                          sc={{
                            outline: true,
                            block: true,
                            background: value.sport_types.includes(sportType) ? 'gray.150' : 'transparent',
                            fontWeight: 400,
                          }}
                          key={sportType}
                        >
                          {t(`common:sport_types.${sportType}`)}
                        </UI.Button>
                      ))}
                    </UI.FormGrid>
                  </UI.FadeIn>
                )}
                <UI.FlexContainer sc={{ justifyContent: 'space-between', py: 3 }}>
                  <UI.Button disabled={isEmpty && !hasChanged} onClick={() => reset()}>
                    <UI.Icon>
                      <Trash />
                    </UI.Icon>
                    {' '}
                    {t('website.reset')}
                  </UI.Button>
                  <UI.Button
                    type="submit"
                    sc={{ brand: hasChanged ? 'secondary' : undefined }}
                  >
                    <UI.Icon>
                      <Check />
                    </UI.Icon>
                    {' '}
                    {t('website.confirm')}
                  </UI.Button>
                </UI.FlexContainer>
              </UI.Card>
            </UI.Form>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {!isEmpty && (
        <UI.Button
          onClick={() => reset()}
          sc={{
            pl: 1,
            pr: 1.5,
            outline: true,
            brand: 'gray.250',
            color: 'gray.800',
            active: true,
          }}
          style={{ borderLeft: 0 }}
        >
          <UI.Icon>
            <X />
          </UI.Icon>
        </UI.Button>
      )}
    </UI.Pill>
  );
};

type Country = GetCalendarPage['eventCountries'][0];

interface CountryFiltersProps extends FiltersProps {
  countries?: string[];
}

const CountryFilters = ({ value: defaultValue, onChange, countries = [] }: CountryFiltersProps) => {
  const { t } = useTranslation();
  const device = useMediaDevice();

  const [value, setValue] = useState(defaultValue);
  const [showing, setShowing] = useState(false);

  const toggleCountry = (sportType: Country) => {
    setValue((value) => ({
      ...value,
      countries: toggleInList(value.countries, sportType),
    }));
  };

  const isEmpty = defaultValue.countries.length === 0;
  const hasChanged = JSON.stringify(defaultValue) !== JSON.stringify(value);

  const reset = () => {
    const emptyValue: FiltersValue = {
      ...defaultValue,
      countries: [],
    };

    setValue(emptyValue);

    if (!showing) {
      onChange(emptyValue);
    }
  };

  const confirm = () => {
    onChange(value);
    setShowing(false);
  };

  const handleOpen = (showing: boolean) => {
    setShowing(showing);
    setValue(defaultValue);
  };

  return (
    <UI.Pill sc={{ mr: 1.5, mt: 1.5 }}>
      <Popover.Root open={showing} onOpenChange={handleOpen}>
        <Popover.Trigger asChild>
          <UI.Button
            sc={{
              outline: true,
              brand: 'gray.250',
              color: 'gray.800',
              pl: 1.5,
              pr: isEmpty ? 1.5 : 0.5,
              active: !isEmpty || showing,
            }}
          >
            <UI.Icon>
              <Globe />
            </UI.Icon>
            {' '}
            {isEmpty && t('website.country')}
            <UI.Delimit delimiter=", ">
              {...defaultValue.countries.map((country) => t(`common:countries.${country}`).split(', ')[0]).slice(0, 3) || [t('website.country')]}
              {defaultValue.countries.length > 3 && '...'}
            </UI.Delimit>
            {isEmpty && (
              <UI.Icon sc={{ ml: 1.5 }}>
                <ChevronDown />
              </UI.Icon>
            )}
          </UI.Button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content style={{ zIndex: 1000 }} side="bottom" align="start">
            <UI.Form onSubmit={() => confirm()}>
              <UI.Card
                sc={{
                  borderRadius: 'md', hasPanels: true, active: true, width: '600px', background: 'white',
                }}
                style={{ maxWidth: device.width - 32 }}
              >
                <UI.FormGrid
                  sc={{ gutter: 0.25, columns: ['1fr', '1fr 1fr'] }}
                  style={{
                    maxHeight: 300,
                    overflowY: 'auto',
                  }}
                >
                  {countries.map((country) => (
                    <UI.Button
                      onClick={() => toggleCountry(country)}
                      sc={{
                        outline: true,
                        block: true,
                        background: value.countries.includes(country) ? 'gray.150' : 'transparent',
                        fontWeight: 400,
                      }}
                      key={country}
                    >
                      {t(`common:countries.${country}`).split(', ')[0]}
                    </UI.Button>
                  ))}
                </UI.FormGrid>
                <UI.FlexContainer sc={{ justifyContent: 'space-between', py: 3 }}>
                  <UI.Button disabled={isEmpty && !hasChanged} onClick={() => reset()}>
                    <UI.Icon>
                      <Trash />
                    </UI.Icon>
                    {' '}
                    {t('website.reset')}
                  </UI.Button>
                  <UI.Button
                    type="submit"
                    sc={{ brand: hasChanged ? 'secondary' : undefined }}
                  >
                    <UI.Icon>
                      <Check />
                    </UI.Icon>
                    {' '}
                    {t('website.confirm')}
                  </UI.Button>
                </UI.FlexContainer>
              </UI.Card>
            </UI.Form>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {!isEmpty && (
        <UI.Button
          onClick={() => reset()}
          sc={{
            pl: 1,
            pr: 1.5,
            outline: true,
            brand: 'gray.250',
            color: 'gray.800',
            active: true,
          }}
          style={{ borderLeft: 0 }}
        >
          <UI.Icon>
            <X />
          </UI.Icon>
        </UI.Button>
      )}
    </UI.Pill>
  );
};

interface FiltersValue {
  search: string;
  sport_types: SportType[];
  distances: DistancePreset[];
  min_distance?: number | null;
  max_distance?: number | null;
  countries: Country[];
  months: string[];
  sort: SortOption | null;
}

interface FiltersProps {
  value: FiltersValue;
  onChange: (value: FiltersValue) => unknown;
}

const DistanceFilters = ({ value: defaultValue, onChange }: FiltersProps) => {
  const { t } = useTranslation();
  const device = useMediaDevice();

  const [value, setValue] = useState(defaultValue);
  const [showing, setShowing] = useState(false);

  const distances = getDistances(defaultValue.sport_types);

  const initialTab = value.min_distance !== null || value.max_distance !== null || distances.length === 0
    ? 'custom' : 'distances';

  const [tab, setTab] = useState<'distances' | 'custom'>(initialTab);

  const toggleDistance = (distance?: DistancePreset) => {
    setValue((value) => ({
      ...value,
      distances: intersection(distances, toggleInList(value.distances, distance)),
      min_distance: null,
      max_distance: null,
    }));
  };

  const handleCustomDistanceChange = (event: ChangeEvent<HTMLInputElement>, key: 'min_distance' | 'max_distance') => {
    const inputValue = event.target.value;

    setValue((value) => ({
      ...value,
      distances: [],
      [key]: inputValue !== '' ? parseInt(inputValue.match(/[\d]/g).join(''), 10) : null,
    }));
  };

  const hasPredefinedDistance = defaultValue.distances.length > 0;
  const hasCustomDistance = defaultValue.min_distance !== null || defaultValue.max_distance !== null;
  const isEmpty = !hasPredefinedDistance && !hasCustomDistance;

  const hasInvalidCustomDistance = value.min_distance !== null && value.max_distance !== null
    && value.min_distance > value.max_distance;

  const hasChanged = JSON.stringify(defaultValue) !== JSON.stringify(value);

  const reset = useCallback(() => {
    const emptyValue: FiltersValue = {
      ...defaultValue,
      distances: [],
      min_distance: null,
      max_distance: null,
    };

    setValue(emptyValue);

    if (!showing) {
      onChange(emptyValue);
    }
  }, [defaultValue, onChange, showing]);

  // Reset distances when the set of allowed distances no longer matches the selected distances.
  useEffect(() => {
    if (difference(defaultValue.distances, distances).length > 0) {
      onChange({
        ...defaultValue,
        distances: intersection(defaultValue.distances, distances),
      });
    }
  }, [defaultValue, distances, onChange]);

  const confirm = () => {
    onChange({
      ...value,
      min_distance: tab === 'custom' ? value.min_distance : null,
      max_distance: tab === 'custom' ? value.max_distance : null,
      distances: tab === 'distances' ? value.distances : [],
    });
    setShowing(false);
  };

  const handleOpen = (showing: boolean) => {
    setShowing(showing);
    setValue(defaultValue);

    setTab(initialTab);
  };

  return (
    <UI.Pill sc={{ mr: 1.5, mt: 1.5 }}>
      <Popover.Root open={showing} onOpenChange={handleOpen}>
        <Popover.Trigger asChild>
          <UI.Button
            sc={{
              outline: true,
              brand: 'gray.250',
              color: 'gray.800',
              pl: 1.5,
              pr: isEmpty ? 1.5 : 0.5,
              active: !isEmpty || showing,
            }}
          >
            <UI.Icon>
              <Maximize2 />
            </UI.Icon>
            {' '}
            {isEmpty && t('website.distance')}
            <UI.Delimit delimiter=", ">
              {hasCustomDistance && formatDistance(defaultValue.min_distance, defaultValue.max_distance, t)}
              {...hasPredefinedDistance ? defaultValue.distances.map((distance) => t(`website.distance_types.${distance}`)).slice(0, 3) : []}
              {hasPredefinedDistance && defaultValue.distances.length > 3 && '...'}
            </UI.Delimit>
            {isEmpty && (
              <UI.Icon sc={{ ml: 1.5 }}>
                <ChevronDown />
              </UI.Icon>
            )}
          </UI.Button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content style={{ zIndex: 1000 }} side="bottom" align="start">
            <UI.Form onSubmit={() => confirm()}>
              <UI.Card
                sc={{
                  borderRadius: 'md', hasPanels: true, active: true, width: '400px', background: 'white',
                }}
                style={{ maxWidth: device.width - 32 }}
              >
                {distances.length > 0 && (
                  <UI.Div sc={{ py: 0, spaceX: 2 }} style={{ margin: '2px 0 -1px 0' }}>
                    <UI.TabButton
                      type="button"
                      onClick={() => setTab('distances')}
                      sc={{ active: tab === 'distances' }}
                    >
                      {t('website.distances')}
                    </UI.TabButton>
                    <UI.TabButton
                      type="button"
                      onClick={() => setTab('custom')}
                      sc={{ active: tab === 'custom' }}
                    >
                      {t('website.custom')}
                    </UI.TabButton>
                  </UI.Div>
                )}
                {distances.length > 0 && tab === 'distances' && (
                  <UI.FadeIn>
                    <UI.FormGrid sc={{ gutter: 0.25, columns: '1fr 1fr' }}>
                      {distances.map((distance) => (
                        <UI.Button
                          onClick={() => toggleDistance(distance)}
                          sc={{
                            outline: true,
                            block: true,
                            background: value.distances.includes(distance) ? 'gray.150' : 'transparent',
                            fontWeight: 400,
                          }}
                          key={distance}
                        >
                          {t(`website.distance_types.${distance}`)}
                        </UI.Button>
                      ))}
                    </UI.FormGrid>
                  </UI.FadeIn>
                )}
                {(distances.length === 0 || tab === 'custom') && (
                  <UI.FadeIn>
                    <UI.FormGrid sc={{ gutter: 0.25, columns: '1fr 1fr' }}>
                      <UI.InputGroup sc={{ invalid: hasInvalidCustomDistance, touched: hasInvalidCustomDistance }}>
                        <UI.InputLabel htmlFor="MinDistanceInput">
                          {t('website.minimum')}
                        </UI.InputLabel>
                        <UI.InputWrapper>
                          <UI.Input
                            value={value.min_distance ?? ''}
                            onChange={(event) => handleCustomDistanceChange(event, 'min_distance')}
                            maxLength={4}
                            inputMode="numeric"
                            id="MinDistanceInput"
                          />
                          <UI.Span>
                            {t('website.km')}
                          </UI.Span>
                        </UI.InputWrapper>
                      </UI.InputGroup>
                      <UI.InputGroup sc={{ invalid: hasInvalidCustomDistance, touched: hasInvalidCustomDistance }}>
                        <UI.InputLabel htmlFor="MaxDistanceInput">
                          {t('website.maximum')}
                        </UI.InputLabel>
                        <UI.InputWrapper>
                          <UI.Input
                            value={value.max_distance ?? ''}
                            onChange={(event) => handleCustomDistanceChange(event, 'max_distance')}
                            maxLength={4}
                            inputMode="numeric"
                            id="MaxDistanceInput"
                          />
                          <UI.Span>
                            {t('website.km')}
                          </UI.Span>
                        </UI.InputWrapper>
                      </UI.InputGroup>
                    </UI.FormGrid>
                  </UI.FadeIn>
                )}
                <UI.FlexContainer sc={{ justifyContent: 'space-between', py: 3 }}>
                  <UI.Button disabled={isEmpty && !hasChanged} onClick={() => reset()}>
                    <UI.Icon>
                      <Trash />
                    </UI.Icon>
                    {' '}
                    {t('website.reset')}
                  </UI.Button>
                  <UI.Button
                    type="submit"
                    sc={{ brand: hasChanged ? 'secondary' : undefined }}
                  >
                    <UI.Icon>
                      <Check />
                    </UI.Icon>
                    {' '}
                    {t('website.confirm')}
                  </UI.Button>
                </UI.FlexContainer>
              </UI.Card>
            </UI.Form>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {!isEmpty && (
        <UI.Button
          onClick={() => reset()}
          sc={{
            pl: 1,
            pr: 1.5,
            outline: true,
            brand: 'gray.250',
            color: 'gray.800',
            active: true,
          }}
          style={{ borderLeft: 0 }}
        >
          <UI.Icon>
            <X />
          </UI.Icon>
        </UI.Button>
      )}
    </UI.Pill>
  );
};

const SearchFilters = ({ value, onChange }: FiltersProps) => {
  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, search: event.target.value });
  };

  const reset = () => {
    onChange({
      ...value,
      search: null,
    });

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <UI.InputGroup sc={{ mb: 0, mr: 1.5, mt: 1.5, width: ['100%', '300px'] }}>
      <UI.InputWrapper>
        <UI.Span>
          <UI.Icon>
            <Search />
          </UI.Icon>
        </UI.Span>
        <UI.DebouncedInput
          value={value.search ?? ''}
          onChange={(event) => handleSearchChange(event)}
          id="SearchInput"
          sc={{ px: 5 }}
          placeholder={t('website.search')}
          aria-label={t('website.search')}
          ref={inputRef}
        />
        {value.search && (
          <UI.Span>
            <UI.A onClick={() => reset()} sc={{ color: 'gray.400' }} title={t('website.reset')} role="button">
              <UI.Icon>
                <XCircle />
              </UI.Icon>
            </UI.A>
          </UI.Span>
        )}
      </UI.InputWrapper>
    </UI.InputGroup>
  );
};

export enum DistancePreset {
  FiveKm = '5km',
  TenKm = '10km',
  HalfMmarathon = 'half_marathon',
  Marathon = 'marathon',
  Ultra = 'ultra',
}

export enum SortOption {
  Upcoming = 'upcoming',
  Trending = 'trending',
}

const SortFilters = ({ value, onChange }: FiltersProps) => {
  const { t } = useTranslation();
  const isTrending = value.sort === SortOption.Trending;

  return (
    <UI.Pill sc={{ mr: 1.5, mt: 1.5 }}>
      <UI.Button
        onClick={() => onChange({ ...value, sort: SortOption.Upcoming })}
        sc={{
          outline: true,
          brand: 'gray.250',
          color: 'gray.800',
          pl: 1.5,
          pr: 1.5,
          active: !isTrending,
        }}
      >
        {t('website.sort_options.upcoming')}
      </UI.Button>
      <UI.Button
        onClick={() => onChange({ ...value, sort: SortOption.Trending })}
        sc={{
          outline: true,
          brand: 'gray.250',
          color: 'gray.800',
          pl: 1.5,
          pr: 1.5,
          active: isTrending,
        }}
        style={{ borderLeft: 0 }}
      >
        {t('website.sort_options.trending')}
      </UI.Button>
    </UI.Pill>
  );
};

const getDistances = (sportTypes: SportType[]) => {
  const distances: DistancePreset[] = [];

  if (sportTypes.length > 0) {
    if (difference(sportTypes, [SportType.running, SportType.trail_running, SportType.obstacle_run]).length === 0) {
      distances.push(DistancePreset.FiveKm);
      distances.push(DistancePreset.TenKm);
      distances.push(DistancePreset.HalfMmarathon);
      distances.push(DistancePreset.Marathon);
      distances.push(DistancePreset.Ultra);
    }
  }

  return distances;
};

const formatDistance = (minDistance: number | null, maxDistance: number | null, t: TFunction) => {
  if (minDistance || maxDistance) {
    if (minDistance === maxDistance) {
      return t('website.n_km', { count: minDistance });
    }

    if (minDistance && maxDistance) {
      return t('website.n_km_range', { min: minDistance, max: maxDistance });
    }

    if (minDistance) {
      return t('website.gte_n_km', { count: minDistance });
    }

    if (maxDistance) {
      return t('website.lte_n_km', { count: maxDistance });
    }
  }

  return t('website.distance');
};

const DateFilters = ({ value: defaultValue, onChange }: FiltersProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const device = useMediaDevice();

  const currentYear = (new Date()).getFullYear();
  const nextYear = currentYear + 1;

  const [year, setYear] = useState(currentYear);

  const months = getMonths(year);

  const [value, setValue] = useState(defaultValue);
  const [showing, setShowing] = useState(false);

  const toggleMonth = (month: string) => {
    setValue((value) => ({
      ...value,
      months: toggleInList(value.months, month).sort(),
    }));
  };

  const hasChanged = JSON.stringify(defaultValue) !== JSON.stringify(value);

  const reset = () => {
    const emptyValue: FiltersValue = {
      ...defaultValue,
      months: [],
    };

    setValue(emptyValue);

    if (!showing) {
      onChange(emptyValue);
    }
  };

  const confirm = () => {
    onChange(value);
    setShowing(false);
  };

  const handleOpen = (showing: boolean) => {
    setShowing(showing);
    setYear(value.months.length > 0 ? parseInt(value.months[0].substring(0, 4), 10) : currentYear);
    setValue(defaultValue);
  };

  const format = useCallback((date: string, format: 'display_year_only' | 'display_month' | 'display_month_only') => (
    formatDate(parseDate(date, { format: 'internal_date', timezone: null }), { format, timezone: null })
  ), [formatDate, parseDate]);

  const isPast = (month: string) => parseDate(month, { format: 'internal_date', timezone: null })
    < startOfMonth(new Date());

  const isEmpty = defaultValue.months.length === 0;

  const displayValue = useMemo(() => {
    const displayValues: string[] = [];
    const currentYear = (new Date()).getFullYear();
    const months = [...defaultValue.months].sort();

    for (let i = 0; i < months.length; i++) {
      if (displayValues.length === 3) {
        displayValues.push('...');
        break;
      }

      const month = months[i];
      const year = parseDate(month, { timezone: null, format: 'internal_date' }).getFullYear();

      displayValues.push(format(month, year === currentYear ? 'display_month_only' : 'display_month'));
    }

    return displayValues.join(', ') || t('website.date');
  }, [defaultValue.months, format, parseDate, t]);

  return (
    <UI.Pill sc={{ mr: 1.5, mt: 1.5 }}>
      <Popover.Root open={showing} onOpenChange={handleOpen}>
        <Popover.Trigger asChild>
          <UI.Button
            sc={{
              outline: true,
              brand: 'gray.250',
              color: 'gray.800',
              pl: 1.5,
              pr: isEmpty ? 1.5 : 0.5,
              active: !isEmpty || showing,
            }}
          >
            <UI.Icon>
              <Calendar />
            </UI.Icon>
            {' '}
            {displayValue}
            {isEmpty && (
              <UI.Icon sc={{ ml: 1.5 }}>
                <ChevronDown />
              </UI.Icon>
            )}
          </UI.Button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content style={{ zIndex: 1000 }} side="bottom" align="start">
            <UI.Form onSubmit={() => confirm()}>
              <UI.Card
                sc={{
                  hasPanels: true,
                  active: true,
                  width: '400px',
                  borderRadius: 'md',
                  background: 'white',
                  mx: [8 / 3, 0],
                }}
                style={{ maxWidth: device.width - 32 }}
              >
                <UI.Div sc={{ py: 0, spaceX: 2 }} style={{ margin: '2px 0 -1px 0' }}>
                  <UI.TabButton
                    type="button"
                    onClick={() => setYear(currentYear)}
                    sc={{ active: year === currentYear }}
                  >
                    {currentYear}
                  </UI.TabButton>
                  <UI.TabButton
                    type="button"
                    onClick={() => setYear(nextYear)}
                    sc={{ active: year === nextYear }}
                  >
                    {nextYear}
                  </UI.TabButton>
                </UI.Div>
                <UI.FadeIn key={year}>
                  <UI.GridContainer sc={{ columns: ['1fr 1fr 1fr', '1fr 1fr 1fr'], gutter: 0.25 }}>
                    {months.map((month) => (
                      <UI.Button
                        onClick={() => toggleMonth(month)}
                        sc={{
                          outline: true,
                          block: true,
                          background: value.months.includes(month) ? 'gray.150' : 'transparent',
                          fontWeight: isPast(month) ? 300 : 400,
                        }}
                        disabled={isPast(month)}
                        key={month}
                      >
                        {format(month, 'display_month_only')}
                      </UI.Button>
                    ))}
                  </UI.GridContainer>
                </UI.FadeIn>
                <UI.FlexContainer sc={{ justifyContent: 'space-between', py: 3 }}>
                  <UI.Button disabled={isEmpty && !hasChanged} onClick={() => reset()}>
                    <UI.Icon>
                      <Trash />
                    </UI.Icon>
                    {' '}
                    {t('website.reset')}
                  </UI.Button>
                  <UI.Button
                    type="submit"
                    sc={{ brand: hasChanged ? 'secondary' : undefined }}
                  >
                    <UI.Icon>
                      <Check />
                    </UI.Icon>
                    {' '}
                    {t('website.confirm')}
                  </UI.Button>
                </UI.FlexContainer>
              </UI.Card>
            </UI.Form>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {!isEmpty && (
        <UI.Button
          onClick={() => reset()}
          sc={{
            pl: 1,
            pr: 1.5,
            outline: true,
            brand: 'gray.250',
            color: 'gray.800',
            active: true,
          }}
          style={{ borderLeft: 0 }}
        >
          <UI.Icon>
            <X />
          </UI.Icon>
        </UI.Button>
      )}
    </UI.Pill>
  );
};

const getMonths = (year: number) => {
  const months: string[] = [];

  for (let i = 1; i <= 12; i++) {
    months.push(`${year}-${i < 10 ? 0 : ''}${i}-01`);
  }

  return months;
};

export default CalendarPage;
